import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useLang } from '../_LangContext';
import { useMapContext } from '../_MapContext';
import { UserLocation } from '../_UserLocation';
import MapZoomDetector from '../leaflet/_MapZoomDetector';
import MapPinManager from './MapPinManager';
import MapZoomController from './MapZoomController';
import ConstructionConfig from '../_ConstructionConfig';
import { mapImageSize, MAP_DEFAULT_ZOOM } from './MapConstants';

L.RasterCoords = require('leaflet-rastercoords');

const RasterCoordsMap = ({ onPinClick }) =>
{
    const map = useMap();
    const { lang, isLocaleReady } = useLang();
    const { bfMapState, autoCenterEnabled, setAutoCenter, targetPinLatLng, hasTargetPin, showTargetPin, selectTargettedPin } = useMapContext();

    const tileLayerRef = useRef(null);
    const pinManagerRef = useRef(null);
    const zoomControllerRef = useRef(null);
    const autoCenterEnabledRef = useRef(autoCenterEnabled);
    const isInitializedRef = useRef(false);
    const areFeaturesAddedRef = useRef(false);

    // Store stable references to avoid re-initialization
    const onPinClickRef = useRef(onPinClick);
    const setAutoCenterRef = useRef(setAutoCenter);
    const showTargetPinRef = useRef(showTargetPin);
    const selectTargettedPinRef = useRef(selectTargettedPin);

    // Update refs when values change
    useEffect(() => {
        onPinClickRef.current = onPinClick;
        setAutoCenterRef.current = setAutoCenter;
        showTargetPinRef.current = showTargetPin;
        selectTargettedPinRef.current = selectTargettedPin;
    });

    // Load construction config
    useEffect(() =>
    {
        ConstructionConfig.loadConfig();
    }, []);

    // Initialize map only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() =>
    {
        if (!map || isInitializedRef.current) return;

        // Setup map
        const rc = new L.RasterCoords(map, [mapImageSize.w, mapImageSize.h]);
        map.setView(rc.unproject([mapImageSize.w * .44, mapImageSize.h]), MAP_DEFAULT_ZOOM);

        // Initialize controllers
        const zoomController = new MapZoomController(map);
        const pinManager = new MapPinManager(map, rc, lang, onPinClickRef.current);
        
        zoomControllerRef.current = zoomController;
        pinManagerRef.current = pinManager;

        // Setup tile layer
        tileLayerRef.current = L.tileLayer(zoomController.getTileURL(bfMapState),
        {
            noWrap: true,
            maxNativeZoom: rc.zoomLevel(),
            detectRetina: true
        }).addTo(map);

        // Map features will be added when locale data is ready (see separate useEffect)

        const customZoomInButton = document.getElementById('ui-btn-zoom-in');
        const customZoomOutButton = document.getElementById('ui-btn-zoom-out');

        // Initialize zoom detector (used for custom zoom buttons)
        new MapZoomDetector(
            map,
            (zoomLevel, targetZoom) => zoomController.handleZoomIn(zoomLevel, targetZoom),
            (zoomLevel, targetZoom) => zoomController.handleZoomOut(zoomLevel, targetZoom),
            customZoomInButton,
            customZoomOutButton
        );

        const onZoomEnd = () => zoomController.onZoomEnd();
        const disableAutoCenterOnMove = () => setAutoCenterRef.current(false);

        // Handle zoom changes
        map.on('zoomend', onZoomEnd);
        map.on("dragstart", disableAutoCenterOnMove);

        // Setup UserLocation integration
        UserLocation.getInstance().map = map;
        UserLocation.getInstance().rasterCoords = rc;

        UserLocation.getInstance().setTargetPin = function(type, item)
        {
            if (!this.map || !this.rasterCoords) return;

            const latLng = this.xyToLatLng(item.x, item.y, 4);
            showTargetPinRef.current(latLng);

            selectTargettedPinRef.current(type, item.id);
        };

        UserLocation.getInstance().setTargetPinByXY = function(x, y, multiplier = 1)
        {
            if (!this.map || !this.rasterCoords) return;

            const latLng = this.xyToLatLng(x, y, multiplier);
            showTargetPinRef.current(latLng);
        };

        UserLocation.getInstance().highlightPin = function(type, item)
        {
            if (!this.map || !this.rasterCoords) return;

            const { id, x, y } = item;
            let pins;

            if (type === 'site')
                pins = pinManager.getSitePinsRef().current;
            else if (type === 'exterior')
                pins = pinManager.getExteriorMapPinsRef().current;
            else if (type === 'food')
                pins = pinManager.getFoodPinsRef().current;

            if (!pins) return;

            const pin = pins.find(p => p.id === id);
            if (pin) pin.highlight();

            this.setViewByXY(x, y, 4);
        };

        UserLocation.getInstance().onLocationUpdate = function(x, y)
        {
            if (!this.map || !this.rasterCoords || !pinManagerRef.current?.getUserPinRef()) return;

            const latLng = this.xyToLatLng(x, y, 1);
            pinManagerRef.current.getUserPinRef().setLatLng(latLng);

            if (autoCenterEnabledRef.current) this.map.setView(latLng);
        };

        isInitializedRef.current = true;

        return () =>
        {
            if (map)
            {
                map.off('zoomend', onZoomEnd);
                map.off('dragstart', disableAutoCenterOnMove);
            }
        };
    },
    [map]); // Only depend on map to prevent re-initialization

    // Add map features when locale data is ready
    useEffect(() =>
    {
        if (!pinManagerRef.current || !zoomControllerRef.current || !isLocaleReady || areFeaturesAddedRef.current) return;

        const addFeatures = async () =>
        {
            await pinManagerRef.current.addAllMapFeatures();
            
            // Set pin references for zoom controller AFTER pins are created
            zoomControllerRef.current.setPinRefs(
                pinManagerRef.current.getSitePinsRef(),
                pinManagerRef.current.getExteriorMapPinsRef()
            );
            
            // Immediately set zoom levels for all pins after creation
            zoomControllerRef.current.onZoomEnd();
            
            areFeaturesAddedRef.current = true;
        };

        // Add features after a small delay to ensure map is ready
        setTimeout(addFeatures, 100);
    },
    [isLocaleReady]);

    // Handle tile layer updates separately
    useEffect(() =>
    {
        if (map && tileLayerRef.current && zoomControllerRef.current)
        {
            tileLayerRef.current.setUrl(zoomControllerRef.current.getTileURL(bfMapState));
        }
    },
    [map, bfMapState]);

    // Handle language updates separately without re-creating pins
    useEffect(() =>
    {
        if (pinManagerRef.current)
        {
            pinManagerRef.current.updateLanguage(lang);
        }
    },
    [lang]);

    useEffect(() =>
    {
        autoCenterEnabledRef.current = autoCenterEnabled;
    },
    [autoCenterEnabled]);

    // Target Pin effects
    useEffect(() =>
    {
        if (pinManagerRef.current?.getTargetPinRef() && targetPinLatLng)
        {
            pinManagerRef.current.getTargetPinRef().setLatLng(targetPinLatLng);
        }
    },
    [targetPinLatLng]);

    useEffect(() =>
    {
        if (pinManagerRef.current?.getTargetPinRef())
        {
            pinManagerRef.current.getTargetPinRef().active = hasTargetPin;
        }
    },
    [hasTargetPin]);

    return null;
};

export default RasterCoordsMap; 